import { Component } from '@theme/component';

/**
 * Necklace-first builder: necklace choice shows that necklace's charms; charm choice shows one panel.
 * Deep links: ?chain=<handle>, ?charm=<handle> (first necklace offering it, in stock first) and #Interest-<pairing key>.
 * A chosen second charm is added together with the necklace in one cart request; without JS the form adds the necklace.
 */
class MaakNecklaceBuilder extends Component {
  #controller;

  connectedCallback() {
    super.connectedCallback();
    this.#bind();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bind();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    super.disconnectedCallback();
  }

  get #groups() {
    return [...this.querySelectorAll('[data-chain-group]')];
  }

  #bind() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    for (const input of this.querySelectorAll('[data-chain-choice]')) {
      input.addEventListener('change', () => this.#showChain(input.value), { signal });
    }
    for (const input of this.querySelectorAll('[data-charm-choice]')) {
      input.addEventListener('change', () => this.#showOption(input.closest('[data-chain-group]'), input.value), { signal });
    }
    for (const form of this.querySelectorAll('[data-builder-form]')) {
      form.addEventListener('submit', event => this.#submit(event), { signal });
    }
    this.#restore();
  }

  #showChain(handle, charm = '') {
    for (const input of this.querySelectorAll('[data-chain-choice]')) input.checked = input.value === handle;
    for (const group of this.#groups) {
      group.hidden = group.dataset.chainGroup !== handle;
      if (!group.hidden) {
        const choices = [...group.querySelectorAll('[data-charm-choice]')];
        const only = choices.length === 1 ? choices[0].value : '';
        this.#showOption(group, charm || only);
      }
    }
  }

  #showOption(group, value) {
    if (!group) return;
    for (const input of group.querySelectorAll('[data-charm-choice]')) input.checked = input.value === value;
    for (const panel of group.querySelectorAll('[data-option]')) panel.hidden = panel.dataset.option !== value;
    const status = this.querySelector('[data-builder-status]');
    if (status) status.textContent = '';
  }

  #restore() {
    const params = new URLSearchParams(location.search);
    const linked = location.hash ? document.getElementById(decodeURIComponent(location.hash.slice(1))) : null;
    const posted = this.querySelector('[data-request-posted]') ?? (linked && this.contains(linked) ? linked : null);
    if (posted) {
      const group = posted.closest('[data-chain-group]');
      const panel = posted.closest('[data-option]');
      this.#showChain(group.dataset.chainGroup, panel.dataset.option);
      posted.closest('form')?.querySelector('details')?.setAttribute('open', '');
      requestAnimationFrame(() => panel.scrollIntoView({ block: 'start' }));
      return;
    }
    const chain = params.get('chain');
    const charm = params.get('charm');
    if (chain && this.#groups.some(g => g.dataset.chainGroup === chain)) return this.#showChain(chain, charm || '');
    if (charm) {
      const offering = this.#groups.filter(g => g.querySelector(`[data-option="${CSS.escape(charm)}"]`));
      const inStock = offering.find(g => g.querySelector(`[data-option="${CSS.escape(charm)}"] [data-builder-form]`));
      const group = inStock ?? offering[0];
      if (group) {
        this.#showChain(group.dataset.chainGroup, charm);
        requestAnimationFrame(() => group.scrollIntoView({ block: 'start' }));
        return;
      }
    }
    for (const group of this.#groups) group.hidden = true;
  }

  async #submit(event) {
    const form = event.target;
    const extra = form.querySelector('[data-extra-charm]')?.value;
    if (!extra) return;
    event.preventDefault();
    const button = form.querySelector('button[type="submit"]');
    if (button) button.disabled = true;
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        signal: this.#controller.signal,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(form.elements.id.value), quantity: 1 }, { id: Number(extra), quantity: 1 }] })
      });
      if (!response.ok) throw new Error('add failed');
      location.href = '/cart';
    } catch (error) {
      if (error.name === 'AbortError') return;
      const status = this.querySelector('[data-builder-status]');
      if (status) status.textContent = this.dataset.error;
      if (button) button.disabled = false;
    }
  }
}

if (!customElements.get('maak-necklace-builder')) customElements.define('maak-necklace-builder', MaakNecklaceBuilder);
